import React, { FC, useEffect } from 'react';

import { Box, Grid, Typography } from '@mui/material';

import {
  adminReports,
  getPublishedSoftwareList,
  publishedSoftwareList,
  searchedPharmacists,
  ShiftSourceItems,
  TypeOfUser,
} from '@pharmaplan/common';
import { dropdownMapper } from '@pharmaplan/common/helpers/utils';
import { FormikProps } from 'formik';
import { useLocation } from 'react-router-dom';
import CustomDateRange from '../../../common/CustomDateRange';
import CustomDropdown from '../../../common/CustomDropdown';
import CustomTextField from '../../../common/CustomTextField';
import FilterSubmitButtons from '../../../common/FilterSubmitButtons';
import CustomAutocomplete from '../../../common/CustomAutocomplete';
import { toOptions } from '../../ExpressBooking/ExpressBooking';
import DownloadPDF from '../../../common/DownloadPDF';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import useAdminReportsHandler from '../../../../hooks/AdminReports/useAdminReportsHandler';
import strings from '../../../../localization';

import useAdminReportsPDF from '../../../../hooks/AdminReports/useAdminReportsPDF';
import useAutoCompleteScroll, {
  AutoCompleteScrollTypes,
} from '../../../../hooks/useAutoCompleteScroll';
import { getDisabledSubmitFilter } from '../../../../selectors/calendarSelector';
import SelectAllHeader from '../../../DynamicTable/SelectAllHeader';
import OwnershipFilters from './OwnershipFilters';

import styles from './style';
import {
  Constants,
  nameLabels,
  ProfessionalTypesDropdown,
} from '../../../../helpers/Constants';

const { bookingReport, requestedReport } = Constants.paths.admin;

interface IAdminReportsHeader {
  formik: FormikProps<any>;
  handleReset: () => void;
  selectAll: boolean;
  handleSelectAll: (toBeSet: boolean) => void;
  handleDelete: () => void;
  selectableData: Array<string>;
}

const AdminReportsHeader: FC<IAdminReportsHeader> = ({
  formik,
  handleReset,
  selectAll,
  handleDelete,
  handleSelectAll,
  selectableData,
}) => {
  const dispatch = useAppDispatch();
  const { menuItems, isAvailabilities, title, isPosted, isOwnership } = useAdminReportsHandler();
  const { pathname } = useLocation();
  const { exportPDF } = useAdminReportsPDF(formik);
  const { handleSubmit, values } = formik ?? {};

  const { onEndReached, pharmacistListData } = useAutoCompleteScroll(
    AutoCompleteScrollTypes.pharmacist,
  );
  const { pharmacists: formikPharmacists } = formik.values ?? {};

  const { startDate, endDate } = values ?? {};

  const disableSubmit = useAppSelector(getDisabledSubmitFilter);
  const pharmacists = useAppSelector(searchedPharmacists);
  const softwares = useAppSelector(publishedSoftwareList);
  const reports = useAppSelector(adminReports);
  const { data } = reports ?? {};

  const noData = data.length === 0;
  const noSelectableData = selectableData?.length === 0;
  const showSource = pathname === bookingReport || pathname === requestedReport;
  const showReplacementType = !isAvailabilities && !isOwnership;

  const showSelectedHeader = !noData && !noSelectableData && isPosted;

  const pharmacistOptions = toOptions(pharmacists);

  const pharmacistOption = formikPharmacists
    ? pharmacistOptions
    : pharmacistListData;

  const placeholder = strings.formatString(
    strings.enterCred,
    `${strings.name}/${strings.email}`,
  );

  useEffect(() => {
    if (isAvailabilities) {
      dispatch(getPublishedSoftwareList());
    }
  }, [dispatch, isAvailabilities]);

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={styles.header}>
        <Typography sx={styles.titleFont}>{title}</Typography>
        <DownloadPDF disabled={noData} onClick={exportPDF} />
      </Box>

      <Box sx={styles.filterContainer}>
        <Grid container xs={9} alignItems="center" wrap="nowrap">
          <CustomDateRange
            startName="startDate"
            endName="endDate"
            formik={formik}
            preventPast={false}
          />

          {isAvailabilities && (
            <>
              <Grid item xs={5}>
                <CustomAutocomplete
                  customAutoCompleteStyle={styles.autocomplete}
                  label={nameLabels().roleLabel}
                  idName="pharmacistId"
                  infiniteScroll={!formikPharmacists}
                  textName="pharmacists"
                  autoCompleteName="pharmacistSelected"
                  onEndReached={onEndReached(TypeOfUser.pharmacist)}
                  placeholder={placeholder as string}
                  formik={formik}
                  options={pharmacistOption}
                />
              </Grid>
              <Grid item xs={5} sx={styles.container}>
                <CustomDropdown
                  label={strings.status}
                  menuItems={menuItems}
                  formik={formik}
                  name="availabilityStatus"
                />
              </Grid>
              <Grid item xs={5} sx={styles.container}>
                <CustomDropdown
                  label={strings.userType}
                  menuItems={ProfessionalTypesDropdown()}
                  formik={formik}
                  name="userType"
                />
              </Grid>
              <Grid item xs={5} sx={styles.container}>
                <CustomDropdown
                  label={strings.software}
                  menuItems={dropdownMapper(softwares, 'softwareId', 'name')}
                  formik={formik}
                  name="softwareId"
                />
              </Grid>
            </>
          )}

          {showReplacementType && (
            <Grid item xs={5} sx={styles.container}>
              <CustomDropdown
                label={strings.replacementType}
                menuItems={menuItems}
                formik={formik}
                name="replacementType"
              />
            </Grid>
          )}

          {isPosted && (
            <Grid item xs={5} sx={styles.container}>
              <CustomTextField
                label={strings.city}
                name="city"
                formik={formik}
              />
            </Grid>
          )}

          {showSource && (
            <Grid item xs={5} sx={styles.container}>
              <CustomDropdown
                label={strings.postedBy}
                menuItems={ShiftSourceItems}
                formik={formik}
                name="source"
              />
            </Grid>
          )}
          <OwnershipFilters formik={formik} isOwnership={isOwnership} />

          <FilterSubmitButtons
            disableSubmit={startDate && endDate && disableSubmit}
            handleReset={handleReset}
          />
        </Grid>
        {showSelectedHeader && (
          <SelectAllHeader
            selectAll={selectAll}
            handleSelectAll={handleSelectAll}
            showDelete
            handleDelete={handleDelete}
          />
        )}
      </Box>
    </Box>
  );
};

export default AdminReportsHeader;
